import * as React from "react";
import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {backgroundColor, calculateRemSize, flex} from "@pg-design/helpers-css";
import {BrandCheckboxCheckedIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {patchRequest} from "@pg-mono/request";
import {apiLink} from "@pg-mono/rp-api-routes";

import {googleRecaptchaId} from "../../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../../app/rp_reducer";
import {LeadModalStep} from "../../../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/ApplicationSource";
import {openLeadModal} from "../../../../lead/state/lead_modal_slice";
import {GTMEventApplicationSourceSection} from "../../../../lead/types/GTMEvenApplicationSourceSection";
import {fetchRecommendedProperties} from "../../../../property/actions/fetch_recommended_properties";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {useAppDispatch} from "../../../../store/hooks";
import {OfferModalLayout} from "../../../components/OfferModalLayout";
import {loadRecaptchaScript} from "../../../helpers/load_recaptcha_script";
import {hitGtmLeadGenerator, LeadGeneratorAction} from "../../../tracking/lead_generator_events";
import {patchDismissLead} from "../../actions/lead_generator_actions";
import {InquiryButtonTypeName} from "../../constants/inquiry_button_type";
import {LeadGeneratorSuccessModalContent} from "./LeadGeneratorSuccessModalContent";

import "./LeadGeneratorRecapthaStyles.css";

interface IOwnProps extends RouteComponentProps<{}> {
    isPropertyForSale: boolean;
}

interface IStateProps {
    property: IPropertyDetail | null;
}

interface IActionsProps {
    patchDismissLead: (uuid: string) => Promise<void | {uuid: string}>;
    fetchRecommendedProperties: (propertyId: number, excludedOfferIds: number[], filters: Record<string, string>) => void;
}

interface IProps extends IOwnProps, IActionsProps, IStateProps {}

declare global {
    interface Window {
        grecaptcha: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    }
}

const LeadGeneratorSuccessInfoC: React.FC<IProps> = ({property, ...props}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (findLGHash()) {
            hitGtmLeadGenerator(LeadGeneratorAction.VIEW);
            openModal();
            loadRecaptchaScript("recaptcha-key", function () {
                if (window && window.grecaptcha) {
                    window.grecaptcha.ready(() => {
                        window.grecaptcha.execute(googleRecaptchaId, {action: "submit"}).then((token: string) => {
                            setUserScoring(token);
                        });
                    });
                }
            });
            // Uncomment this at the end of the task. This code close CPL modal so it's inconvenient during development process
            // setTimeout(() => closeModal(), LeadCancelDisplayTime);
        }
    }, []);

    const setUserScoring = (token: string) => {
        const lgHash = findLGHash();
        const uuid = lgHash && lgHash.split("=")[1];
        if (!uuid) {
            return;
        }

        const openDate = new Date();
        const updateLeadApiLink = apiLink.application.update({});

        return patchRequest({}, updateLeadApiLink({uuid}), {recaptcha_token: token, schedule_opened_date: openDate})
            .then((application: {uuid: string}) => {
                return application;
            })
            .catch((err) => {
                console.warn("Can not update application", err);
            });
    };

    const removeCPLFromUrl = () => {
        const url = props.location.pathname;
        props.history.replace(url);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        removeCPLFromUrl();
    };

    const openModal = () => setIsModalOpen(true);

    const findLGHash = () => {
        const hashes = props.location.hash.split("#");
        return hashes.find((hash) => hash.includes("lg"));
    };
    const onDismissLead = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        const lgHash = findLGHash();
        const uuid = lgHash && lgHash.split("=")[1];
        if (uuid) {
            hitGtmLeadGenerator(LeadGeneratorAction.AGREEMENT_OFF);
            props.patchDismissLead(uuid);
        }
        closeModal();
    };

    const onRecommendedClick = () => {
        closeModal();
        dispatch(
            openLeadModal({
                vendorId: property?.offer.vendor.id as number,
                offerId: property?.offer.id || null,
                propertyId: property?.id || null,
                source: ApplicationSource.ApplicationOfferScheduleMeeting,
                sourceSection: ApplicationSourceSection.BODY,
                affiliation: "CPL",
                initialStep: LeadModalStep.MULTILEAD,
                preventClosingWhenNoData: true,
                customSourceSection: "Multilead - CPL",
                gtmMeta: {openButtonSourceText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS, openButtonSource: GTMEventApplicationSourceSection.CPL_MODAL}
            })
        );
    };

    useEffect(() => {
        if (property && findLGHash() && props.isPropertyForSale) {
            props.fetchRecommendedProperties(property.id, [property.offer.id], {});
        }
    }, []);

    return (
        <>
            <SystemModal
                isOpen={isModalOpen}
                onModalClose={() => {
                    closeModal();
                    hitGtmLeadGenerator(LeadGeneratorAction.MODAL_CLOSE);
                }}
                css={[modalHolder]}
                variant="fit"
            >
                <OfferModalLayout mobileImageOpened={false} css={backgroundColor("#fff")}>
                    <SystemModal.Header>
                        <div css={headingStyle}>
                            <BrandCheckboxCheckedIcon size="4.4" wrapperColor={theme.colors.primary} wrapperSize="4.4" wrapperType="circle" />
                            <Text variant="headline_2">Twoje zgłoszenie zostało wysłane</Text>
                        </div>
                    </SystemModal.Header>
                    <SystemModal.Content css={contentWrap}>
                        <LeadGeneratorSuccessModalContent
                            onCloseClick={closeModal}
                            onDismissLinkClick={onDismissLead}
                            onRecommendedClick={onRecommendedClick}
                        />
                    </SystemModal.Content>
                </OfferModalLayout>
            </SystemModal>
        </>
    );
};

const LeadGeneratorSuccessConnected = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(LeadGeneratorSuccessInfoC);
export const LeadGeneratorSuccessInfo = withRouter<IOwnProps, typeof LeadGeneratorSuccessConnected>(LeadGeneratorSuccessConnected);

/**
 * Connect
 */

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        property: state.property.property
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators(
            {
                patchDismissLead,
                fetchRecommendedProperties
            },
            dispatch
        )
    };
}

const modalHolder = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    inset: 0;

    @media (min-width: ${theme.breakpoints.md}) {
        inset: 4rem auto;
        overflow: visible;
    }
`;

const headingStyle = (theme: Theme) => css`
    ${flex("center")};
    color: ${theme.colors.gray[800]};
    text-align: left;
    align-self: flex-start;
    gap: ${calculateRemSize(1)};
`;

const contentWrap = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        overflow: visible;
        min-height: 0;

        > div {
            height: 100%;
            overflow-y: auto;
        }
    }
`;
import * as React from "react";
import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {backgroundColor, calculateRemSize, flex} from "@pg-design/helpers-css";
import {BrandCheckboxCheckedIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {patchRequest} from "@pg-mono/request";
import {apiLink} from "@pg-mono/rp-api-routes";

import {googleRecaptchaId} from "../../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../../app/rp_reducer";
import {LeadModalStep} from "../../../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/ApplicationSource";
import {openLeadModal} from "../../../../lead/state/lead_modal_slice";
import {GTMEventApplicationSourceSection} from "../../../../lead/types/GTMEvenApplicationSourceSection";
import {fetchRecommendedProperties} from "../../../../property/actions/fetch_recommended_properties";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {useAppDispatch} from "../../../../store/hooks";
import {OfferModalLayout} from "../../../components/OfferModalLayout";
import {loadRecaptchaScript} from "../../../helpers/load_recaptcha_script";
import {hitGtmLeadGenerator, LeadGeneratorAction} from "../../../tracking/lead_generator_events";
import {patchDismissLead} from "../../actions/lead_generator_actions";
import {InquiryButtonTypeName} from "../../constants/inquiry_button_type";
import {LeadGeneratorSuccessModalContent} from "./LeadGeneratorSuccessModalContent";

import "./LeadGeneratorRecapthaStyles.css";

interface IOwnProps extends RouteComponentProps<{}> {
    isPropertyForSale: boolean;
}

interface IStateProps {
    property: IPropertyDetail | null;
}

interface IActionsProps {
    patchDismissLead: (uuid: string) => Promise<void | {uuid: string}>;
    fetchRecommendedProperties: (propertyId: number, excludedOfferIds: number[], filters: Record<string, string>) => void;
}

interface IProps extends IOwnProps, IActionsProps, IStateProps {}

declare global {
    interface Window {
        grecaptcha: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    }
}

const LeadGeneratorSuccessInfoC: React.FC<IProps> = ({property, ...props}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (findLGHash()) {
            hitGtmLeadGenerator(LeadGeneratorAction.VIEW);
            openModal();
            loadRecaptchaScript("recaptcha-key", function () {
                if (window && window.grecaptcha) {
                    window.grecaptcha.ready(() => {
                        window.grecaptcha.execute(googleRecaptchaId, {action: "submit"}).then((token: string) => {
                            setUserScoring(token);
                        });
                    });
                }
            });
            // Uncomment this at the end of the task. This code close CPL modal so it's inconvenient during development process
            // setTimeout(() => closeModal(), LeadCancelDisplayTime);
        }
    }, []);

    const setUserScoring = (token: string) => {
        const lgHash = findLGHash();
        const uuid = lgHash && lgHash.split("=")[1];
        if (!uuid) {
            return;
        }

        const openDate = new Date();
        const updateLeadApiLink = apiLink.application.update({});

        return patchRequest({}, updateLeadApiLink({uuid}), {recaptcha_token: token, schedule_opened_date: openDate})
            .then((application: {uuid: string}) => {
                return application;
            })
            .catch((err) => {
                console.warn("Can not update application", err);
            });
    };

    const removeCPLFromUrl = () => {
        const url = props.location.pathname;
        props.history.replace(url);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        removeCPLFromUrl();
    };

    const openModal = () => setIsModalOpen(true);

    const findLGHash = () => {
        const hashes = props.location.hash.split("#");
        return hashes.find((hash) => hash.includes("lg"));
    };
    const onDismissLead = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        const lgHash = findLGHash();
        const uuid = lgHash && lgHash.split("=")[1];
        if (uuid) {
            hitGtmLeadGenerator(LeadGeneratorAction.AGREEMENT_OFF);
            props.patchDismissLead(uuid);
        }
        closeModal();
    };

    const onRecommendedClick = () => {
        closeModal();
        dispatch(
            openLeadModal({
                vendorId: property?.offer.vendor.id as number,
                offerId: property?.offer.id || null,
                propertyId: property?.id || null,
                source: ApplicationSource.ApplicationOfferScheduleMeeting,
                sourceSection: ApplicationSourceSection.BODY,
                affiliation: "CPL",
                initialStep: LeadModalStep.MULTILEAD,
                preventClosingWhenNoData: true,
                customSourceSection: "Multilead - CPL",
                gtmMeta: {openButtonSourceText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS, openButtonSource: GTMEventApplicationSourceSection.CPL_MODAL}
            })
        );
    };

    useEffect(() => {
        if (property && findLGHash() && props.isPropertyForSale) {
            props.fetchRecommendedProperties(property.id, [property.offer.id], {});
        }
    }, []);

    return (
        <>
            <SystemModal
                isOpen={isModalOpen}
                onModalClose={() => {
                    closeModal();
                    hitGtmLeadGenerator(LeadGeneratorAction.MODAL_CLOSE);
                }}
                css={[modalHolder]}
                variant="fit"
            >
                <OfferModalLayout mobileImageOpened={false} css={backgroundColor("#fff")}>
                    <SystemModal.Header>
                        <div css={headingStyle}>
                            <BrandCheckboxCheckedIcon size="4.4" wrapperColor={theme.colors.primary} wrapperSize="4.4" wrapperType="circle" />
                            <Text variant="headline_2">Twoje zgłoszenie zostało wysłane</Text>
                        </div>
                    </SystemModal.Header>
                    <SystemModal.Content css={contentWrap}>
                        <LeadGeneratorSuccessModalContent
                            onCloseClick={closeModal}
                            onDismissLinkClick={onDismissLead}
                            onRecommendedClick={onRecommendedClick}
                        />
                    </SystemModal.Content>
                </OfferModalLayout>
            </SystemModal>
        </>
    );
};

const LeadGeneratorSuccessConnected = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(LeadGeneratorSuccessInfoC);
export const LeadGeneratorSuccessInfo = withRouter<IOwnProps, typeof LeadGeneratorSuccessConnected>(LeadGeneratorSuccessConnected);

/**
 * Connect
 */

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        property: state.property.property
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators(
            {
                patchDismissLead,
                fetchRecommendedProperties
            },
            dispatch
        )
    };
}

const modalHolder = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    inset: 0;

    @media (min-width: ${theme.breakpoints.md}) {
        inset: 4rem auto;
        overflow: visible;
    }
`;

const headingStyle = (theme: Theme) => css`
    ${flex("center")};
    color: ${theme.colors.gray[800]};
    text-align: left;
    align-self: flex-start;
    gap: ${calculateRemSize(1)};
`;

const contentWrap = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        overflow: visible;
        min-height: 0;

        > div {
            height: 100%;
            overflow-y: auto;
        }
    }
`;
